import {
  UserGroupDataQualityField,
  type UserGroupDataQualityIssueRecord,
  UserGroupDataQualityProblem,
  type UserGroupDataQualityRecord
} from '@navio/nsdk';

export type UserGroupDataQualityPromptTranslations = {
  title: (userGroup: UserGroupDataQualityRecord) => string;
  /** Lead-in explaining why the prompt shows; rendered before the issue list. */
  description: string;
  /** Title for the per-member list. Rendered only when member rows are shown. */
  membersTitle: string;
  /** Field name on its own, interpolated into `problemTemplates`. */
  fieldLabels: Record<UserGroupDataQualityField, string>;
  /** Default wording per problem; the same sentence for every field. */
  problemTemplates: Record<UserGroupDataQualityProblem, (fieldLabel: string) => string>;
  /** Only the cells whose wording differs from the template. */
  issueOverrides?: Partial<Record<UserGroupDataQualityField, Partial<Record<UserGroupDataQualityProblem, string>>>>;
  errorTitle: string;
  errorMessage: string;
};

/**
 * Issue copy is a field x problem cross-product, so it is composed rather than
 * enumerated: 9 field names + 3 sentences + the few cells that read differently,
 * instead of 27 hand-written strings per app. The `Record` typing on
 * `fieldLabels` and `problemTemplates` is the completeness guard — a new backend
 * field or problem fails every app at compile time.
 */
export const getUserGroupDataQualityIssueLabel = (
  texts: UserGroupDataQualityPromptTranslations,
  issue: UserGroupDataQualityIssueRecord
): string =>
  texts.issueOverrides?.[issue.field]?.[issue.problem] ??
  texts.problemTemplates[issue.problem](texts.fieldLabels[issue.field]);

/**
 * Several accounts may use one mobile number. Sign-in already picks the
 * account, so a shared number is not a booking-details problem.
 */
export const isSharedPhoneNumberIssue = (issue: Pick<UserGroupDataQualityIssueRecord, 'field' | 'problem'>): boolean =>
  issue.field === UserGroupDataQualityField.MOBILE_PHONE_NUMBER &&
  issue.problem === UserGroupDataQualityProblem.DUPLICATE;

/**
 * Plain-English texts for the backoffice apps. The customer app has lingui, so
 * it declares its own with `t` macros — the shape above is what keeps the two in step.
 */
export const defaultUserGroupDataQualityTexts: UserGroupDataQualityPromptTranslations = {
  title: (userGroup) => `Booking details for ${userGroup.userGroupName} are bad or stale`,
  description: 'Update them where you can.',
  membersTitle: 'Members with incomplete details',
  fieldLabels: {
    [UserGroupDataQualityField.EMAIL]: 'Email',
    [UserGroupDataQualityField.INVOICE_ADDRESS]: 'Invoice address',
    [UserGroupDataQualityField.MOBILE_PHONE_NUMBER]: 'Mobile phone number',
    [UserGroupDataQualityField.NAME]: 'Customer name',
    [UserGroupDataQualityField.ORGANIZATION_NUMBER]: 'Organization number',
    [UserGroupDataQualityField.PRIMARY_CONTACT]: 'Primary contact',
    [UserGroupDataQualityField.LEASE_CONTACT_NAME]: 'Lease contact name',
    [UserGroupDataQualityField.LEASE_CONTACT_MOBILE_PHONE_NUMBER]: 'Lease contact mobile phone number',
    [UserGroupDataQualityField.LEASE_CONTACT_EMAIL]: 'Lease contact email'
  },
  problemTemplates: {
    [UserGroupDataQualityProblem.MISSING]: (field) => `${field} is missing`,
    [UserGroupDataQualityProblem.INVALID]: (field) => `${field} is invalid`,
    [UserGroupDataQualityProblem.DUPLICATE]: (field) => `${field} is also used by another account`
  },
  issueOverrides: {
    [UserGroupDataQualityField.INVOICE_ADDRESS]: {
      [UserGroupDataQualityProblem.INVALID]: 'Invoice address is incomplete',
      [UserGroupDataQualityProblem.DUPLICATE]: 'Invoice address is shared with another account'
    },
    [UserGroupDataQualityField.MOBILE_PHONE_NUMBER]: {
      [UserGroupDataQualityProblem.INVALID]: 'Phone number must be a valid mobile number'
    },
    [UserGroupDataQualityField.LEASE_CONTACT_MOBILE_PHONE_NUMBER]: {
      [UserGroupDataQualityProblem.INVALID]: 'Lease contact phone number must be a valid mobile number'
    }
  },
  errorTitle: 'Could not check booking details',
  errorMessage: 'Try refreshing the page.'
};
